import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import axios from 'axios';
import { toast } from 'sonner';
import {
  CheckSquare, Plus, Search, Filter, ArrowLeft,
  Clock, User, Calendar, AlertTriangle, Loader2,
  MoreVertical, Edit, Trash2, MessageSquare, RefreshCw
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

const API = process.env.REACT_APP_BACKEND_URL;

const COLUMNS = [
  { id: 'todo', title: 'To Do', color: 'bg-slate-500' },
  { id: 'in_progress', title: 'In Progress', color: 'bg-blue-500' },
  { id: 'completed', title: 'Completed', color: 'bg-emerald-500' },
  { id: 'verified', title: 'Verified', color: 'bg-purple-500' }
];

const TaskBoard = () => {
  const { token, user } = useAuth();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [board, setBoard] = useState({
    todo: [],
    in_progress: [],
    completed: [],
    verified: []
  });
  const [filters, setFilters] = useState({
    search: '',
    priority: 'all',
    owner: 'all'
  });
  const [users, setUsers] = useState([]);
  const [createDialog, setCreateDialog] = useState(false);
  const [editDialog, setEditDialog] = useState({ open: false, task: null });
  const [newTask, setNewTask] = useState({
    title: '',
    description: '',
    owner_id: '',
    priority: 'medium',
    due_date: ''
  });

  const fetchBoard = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({ token });
      
      const res = await axios.get(`${API}/api/meeting-intelligence/tasks/board?${params}`);
      setBoard(res.data.board);
    } catch (err) {
      console.error('Error fetching board:', err);
      toast.error('Failed to load task board');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchBoard();
    
    // Fetch users for assignment
    const fetchUsers = async () => {
      try {
        const res = await axios.get(`${API}/api/users?token=${token}`);
        setUsers(res.data.users || []);
      } catch (err) {
        console.error('Error fetching users:', err);
      }
    };
    fetchUsers();
  }, [token, fetchBoard]);

  const updateTaskStatus = async (taskId, newStatus) => {
    try {
      await axios.put(
        `${API}/api/meeting-intelligence/tasks/${taskId}?token=${token}`,
        { status: newStatus }
      );
      
      toast.success('Task updated');
      fetchBoard();
    } catch (err) {
      console.error('Error updating task:', err);
      toast.error('Failed to update task');
    }
  };

  const createTask = async () => {
    if (!newTask.title.trim()) {
      toast.error('Task title is required');
      return;
    }
    
    try {
      await axios.post(
        `${API}/api/meeting-intelligence/tasks?token=${token}`,
        {
          ...newTask,
          due_date: newTask.due_date ? new Date(newTask.due_date).toISOString() : null
        }
      );
      
      toast.success('Task created');
      setCreateDialog(false);
      setNewTask({
        title: '',
        description: '',
        owner_id: '',
        priority: 'medium',
        due_date: ''
      });
      fetchBoard();
    } catch (err) {
      console.error('Error creating task:', err);
      toast.error('Failed to create task');
    }
  };

  const filterTasks = (tasks) => {
    return tasks.filter(task => {
      if (filters.search && !task.title.toLowerCase().includes(filters.search.toLowerCase())) {
        return false;
      }
      if (filters.priority !== 'all' && task.priority !== filters.priority) {
        return false;
      }
      if (filters.owner !== 'all' && task.owner_id !== filters.owner) {
        return false;
      }
      return true;
    });
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'border-l-red-500 bg-red-50 dark:bg-red-900/10';
      case 'medium': return 'border-l-amber-500 bg-amber-50 dark:bg-amber-900/10';
      default: return 'border-l-slate-400 bg-slate-50 dark:bg-slate-800';
    }
  };

  const totalTasks = Object.values(board).reduce((sum, col) => sum + col.length, 0);

  return (
    <div className="p-6 space-y-6" data-testid="task-board">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate('/meeting-intelligence')}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-slate-900 dark:text-white flex items-center gap-3">
              <CheckSquare className="h-7 w-7 text-amber-600" />
              Task Board
            </h1>
            <p className="text-slate-500 mt-1">{totalTasks} tasks total</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Button variant="outline" size="sm" onClick={fetchBoard}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
          <Button 
            onClick={() => setCreateDialog(true)}
            className="bg-amber-600 hover:bg-amber-700"
          >
            <Plus className="h-4 w-4 mr-2" />
            New Task
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center gap-4">
            <div className="relative flex-1 min-w-[200px] max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                placeholder="Search tasks..."
                value={filters.search}
                onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                className="pl-10"
              />
            </div>
            
            <Select
              value={filters.priority}
              onValueChange={(v) => setFilters(prev => ({ ...prev, priority: v }))}
            >
              <SelectTrigger className="w-[140px]">
                <SelectValue placeholder="Priority" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Priorities</SelectItem>
                <SelectItem value="high">High</SelectItem>
                <SelectItem value="medium">Medium</SelectItem>
                <SelectItem value="low">Low</SelectItem>
              </SelectContent>
            </Select>
            
            <Select
              value={filters.owner}
              onValueChange={(v) => setFilters(prev => ({ ...prev, owner: v }))}
            >
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Owner" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Owners</SelectItem>
                <SelectItem value={user?.id}>My Tasks</SelectItem>
                {users.map(u => (
                  <SelectItem key={u.id} value={u.id}>{u.full_name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Kanban Board */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-amber-600" />
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {COLUMNS.map(column => {
            const tasks = filterTasks(board[column.id] || []);
            
            return (
              <div key={column.id} className="space-y-3">
                {/* Column Header */}
                <div className="flex items-center justify-between p-3 bg-slate-100 dark:bg-slate-800 rounded-lg">
                  <div className="flex items-center gap-2">
                    <div className={`w-3 h-3 rounded-full ${column.color}`}></div>
                    <h3 className="font-semibold text-slate-900 dark:text-white">{column.title}</h3>
                  </div>
                  <Badge variant="secondary">{tasks.length}</Badge>
                </div>
                
                {/* Tasks */}
                <div className="space-y-2 min-h-[200px]">
                  {tasks.length === 0 ? (
                    <div className="p-4 text-center text-slate-400 text-sm border-2 border-dashed border-slate-200 dark:border-slate-700 rounded-lg">
                      No tasks
                    </div>
                  ) : (
                    tasks.map(task => (
                      <Card 
                        key={task.id}
                        className={`border-l-4 ${getPriorityColor(task.priority)} cursor-pointer hover:shadow-md transition-shadow`}
                      >
                        <CardContent className="p-3">
                          <div className="flex items-start justify-between gap-2">
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-slate-900 dark:text-white text-sm truncate">
                                {task.title}
                              </p>
                              {task.description && (
                                <p className="text-xs text-slate-500 mt-1 line-clamp-2">
                                  {task.description}
                                </p>
                              )}
                            </div>
                            
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="sm" className="h-6 w-6 p-0">
                                  <MoreVertical className="h-3 w-3" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                {column.id !== 'todo' && (
                                  <DropdownMenuItem onClick={() => updateTaskStatus(task.id, 'todo')}>
                                    Move to To Do
                                  </DropdownMenuItem>
                                )}
                                {column.id !== 'in_progress' && (
                                  <DropdownMenuItem onClick={() => updateTaskStatus(task.id, 'in_progress')}>
                                    Move to In Progress
                                  </DropdownMenuItem>
                                )}
                                {column.id !== 'completed' && (
                                  <DropdownMenuItem onClick={() => updateTaskStatus(task.id, 'completed')}>
                                    Move to Completed
                                  </DropdownMenuItem>
                                )}
                                {column.id !== 'verified' && (
                                  <DropdownMenuItem onClick={() => updateTaskStatus(task.id, 'verified')}>
                                    Move to Verified
                                  </DropdownMenuItem>
                                )}
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>
                          
                          <div className="flex items-center gap-2 mt-3 flex-wrap">
                            <Badge variant={
                              task.priority === 'high' ? 'destructive' :
                              task.priority === 'medium' ? 'default' : 'secondary'
                            } className="text-[10px] px-1.5 py-0">
                              {task.priority}
                            </Badge>
                            
                            {task.owner_name && (
                              <span className="text-[10px] text-slate-500 flex items-center gap-1">
                                <User className="h-3 w-3" />
                                {task.owner_name}
                              </span>
                            )}
                            
                            {task.due_date && (
                              <span className={`text-[10px] flex items-center gap-1 ${
                                new Date(task.due_date) < new Date() && task.status !== 'completed' && task.status !== 'verified'
                                  ? 'text-red-500'
                                  : 'text-slate-500'
                              }`}>
                                <Clock className="h-3 w-3" />
                                {new Date(task.due_date).toLocaleDateString()}
                              </span>
                            )}
                            
                            {task.source === 'meeting' && (
                              <Badge variant="outline" className="text-[10px] px-1.5 py-0">
                                Meeting
                              </Badge>
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    ))
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Create Task Dialog */}
      <Dialog open={createDialog} onOpenChange={setCreateDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Create New Task</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Title *</Label>
              <Input
                value={newTask.title}
                onChange={(e) => setNewTask(prev => ({ ...prev, title: e.target.value }))}
                placeholder="Task title"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                value={newTask.description}
                onChange={(e) => setNewTask(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Task description"
                rows={3}
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Owner</Label>
                <Select
                  value={newTask.owner_id}
                  onValueChange={(v) => setNewTask(prev => ({ ...prev, owner_id: v }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select owner" />
                  </SelectTrigger>
                  <SelectContent>
                    {users.map(u => (
                      <SelectItem key={u.id} value={u.id}>{u.full_name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label>Priority</Label>
                <Select
                  value={newTask.priority}
                  onValueChange={(v) => setNewTask(prev => ({ ...prev, priority: v }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="low">Low</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Due Date</Label>
              <Input
                type="date"
                value={newTask.due_date}
                onChange={(e) => setNewTask(prev => ({ ...prev, due_date: e.target.value }))}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateDialog(false)}>
              Cancel
            </Button>
            <Button onClick={createTask} className="bg-amber-600 hover:bg-amber-700">
              Create Task
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TaskBoard;
